/**
 * V2 admin — Yoinks tab (V1 admin-yoinks.js parity).
 *
 * Every yoink, searchable and sortable, 25 per page. The user and
 * category cells are live: tap to transfer the yoink to another
 * explorer or move it to another (taggable) category. Delete removes
 * storage images first, then the row.
 *
 * Personal yoinks (docs/JOURNAL-PLAN.md §11) are the same rows with a
 * personal_category_id instead of a category_id — the Category cell says
 * "🔒 name (personal)" and the picker offers the OWNER's personal
 * categories alongside the site ones, so recategorizing crosses the line
 * in either direction. The J6 queue is this tab searched for
 * "Uncategorized". Shelf names come from a flat personal_categories read,
 * not an embed — the one query this tab can't afford to 400 pre-migration.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { confirmDialog } from './confirm.js';
import { createPersonalCategoryFor } from '../journal/journal.js';
import { fetchAll } from './data.js';

const PER_PAGE = 25;

/** Search + sort (V1 applyYoinkFilter). Exported for tests. */
export function filterYoinks(yoinks, { search = '', sortColumn = 'created_at', sortDirection = 'desc' } = {}) {
  let result = [...yoinks];
  if (search) {
    const term = search.toLowerCase();
    result = result.filter((y) =>
      (y.title || '').toLowerCase().includes(term) ||
      y.userName.toLowerCase().includes(term) ||
      y.categoryName.toLowerCase().includes(term) ||
      y.locationName.toLowerCase().includes(term));
  }
  result.sort((a, b) => {
    const pick = (y) => {
      switch (sortColumn) {
        case 'title': return (y.title || '').toLowerCase();
        case 'user': return y.userName.toLowerCase();
        case 'category': return y.categoryName.toLowerCase();
        case 'location': return y.locationName.toLowerCase();
        default: return new Date(y.created_at).getTime();
      }
    };
    const aVal = pick(a);
    const bVal = pick(b);
    if (sortColumn === 'created_at') return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  return result;
}

export function mount(el, ctx) {
  const { router } = ctx;
  let destroyed = false;
  let all = [];
  let loaded = false;
  let search = '';
  let sortColumn = 'created_at';
  let sortDirection = 'desc';
  let page = 1;
  let picker = null; // { kind: 'user'|'category', yoinkId, term }
  let usersForTransfer = [];
  let catsForEdit = [];
  let shelves = []; // every personal category (admin read-all) — names + owners

  async function load() {
    const baseSelect = 'id,title,description,images,created_at,user_id,category_id,location_id,social_url,' +
      'public_profiles(id,full_name,username),categories:category_id(id,name,emoji),' +
      'locations:location_id(id,display_name)';
    let data;
    try {
      // Shelf names ride a flat read, not an embed — and both reads fall
      // back so the tab still answers if 20260811d hasn't been run yet
      [data, shelves] = await Promise.all([
        fetchAll('yoinks', { select: `${baseSelect},personal_category_id`, order: 'created_at.desc' }),
        fetchAll('personal_categories', { select: 'id,owner_id,name,emoji,slug', order: 'created_at.asc' })
          .catch(() => []),
      ]);
    } catch {
      data = await fetchAll('yoinks', { select: baseSelect, order: 'created_at.desc' });
      shelves = [];
    }
    const shelfById = new Map(shelves.map((s) => [s.id, s]));
    all = data.map((y) => {
      const shelf = y.personal_category_id ? shelfById.get(y.personal_category_id) : null;
      return {
        ...y,
        personal: !!y.personal_category_id,
        userName: y.public_profiles?.full_name || y.public_profiles?.username || 'Unknown',
        categoryName: shelf ? shelf.name : y.categories?.name || (y.personal_category_id ? 'Personal' : 'Uncategorized'),
        categoryEmoji: shelf ? (shelf.emoji || '🔒') : y.categories?.emoji || '📍',
        locationName: y.locations?.display_name || 'Unknown',
      };
    });
    loaded = true;
  }

  async function ensureUsers() {
    if (usersForTransfer.length) return;
    const data = await fetchAll('profiles', { select: 'id,full_name,username', order: 'full_name.asc' });
    usersForTransfer = data.map((u) => ({ ...u, displayName: u.full_name || u.username || '(No name)' }));
  }

  async function ensureCats() {
    if (catsForEdit.length) return;
    catsForEdit = await fetchAll('categories', { select: 'id,name,emoji,can_tag', order: 'name.asc' });
  }

  const arrow = (col) => (sortColumn === col ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '');

  function shown() {
    return filterYoinks(all, { search, sortColumn, sortDirection });
  }

  function pickerListHtml() {
    const term = (picker.term || '').toLowerCase();
    if (picker.kind === 'user') {
      const users = usersForTransfer.filter((u) => !term || u.displayName.toLowerCase().includes(term));
      if (!users.length) return '<p class="admin-muted">No explorers match.</p>';
      const current = all.find((y) => y.id === picker.yoinkId)?.user_id;
      return users.map((u) => `
        <button type="button" class="admin-transfer-row" data-pick-user="${u.id}" data-pick-name="${escapeHtml(u.displayName)}">
          <span>${escapeHtml(u.displayName)}</span>
          ${u.id === current ? '<span class="admin-cell-dim">(current)</span>' : ''}
        </button>`).join('');
    }
    const yoink = all.find((y) => y.id === picker.yoinkId);
    const cats = catsForEdit.filter((c) => c.can_tag !== false)
      .filter((c) => !term || c.name.toLowerCase().includes(term));
    // The OWNER's personal categories ride the same picker (§11): moving
    // a yoink onto one makes it personal; onto a site category, public.
    const ownShelves = shelves.filter((s) => s.owner_id === yoink?.user_id)
      .filter((s) => !term || s.name.toLowerCase().includes(term));
    // Typed something with no exact home? Make it a shelf of the OWNER's
    // right here (20260811f lets admins insert for anyone) and file the
    // yoink onto it in one motion — the old convert dialog's find-or-
    // create, now living inside the ordinary picker.
    const createName = (picker.term || '').trim();
    const createRow = createName ? `
      <button type="button" class="admin-transfer-row" data-picker-create-shelf="${escapeHtml(createName)}">
        <span>➕ New personal category “${escapeHtml(createName)}” for ${escapeHtml(yoink?.userName || 'them')}</span>
      </button>` : '';
    if (!cats.length && !ownShelves.length && !createRow) return '<p class="admin-muted">No categories match.</p>';
    return cats.map((c) => `
      <button type="button" class="admin-transfer-row" data-pick-cat="${c.id}">
        <span>${c.emoji || '📍'} ${escapeHtml(c.name)}</span>
        ${c.id === yoink?.category_id ? '<span class="admin-cell-dim">(current)</span>' : ''}
      </button>`).join('')
      + ownShelves.map((s) => `
      <button type="button" class="admin-transfer-row" data-pick-shelf="${s.id}">
        <span>${s.emoji || '🔒'} ${escapeHtml(s.name)} <span class="admin-cell-dim">(personal)</span></span>
        ${s.id === yoink?.personal_category_id ? '<span class="admin-cell-dim">(current)</span>' : ''}
      </button>`).join('')
      + createRow;
  }

  function render() {
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Fetching the field reports…</div>';
      return;
    }
    const list = shown();
    const totalPages = Math.ceil(list.length / PER_PAGE) || 1;
    if (page > totalPages) page = totalPages;
    const pageRows = list.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    el.innerHTML = `
      <div class="admin-card admin-card-span">
        <div class="admin-toolbar">
          <input type="search" class="admin-search" data-yoink-search aria-label="Search yoinks" placeholder="Search yoinks…"
            value="${escapeHtml(search)}" autocomplete="off">
          <span class="admin-muted">${list.length} of ${all.length}</span>
          <span class="admin-toolbar-spacer"></span>
          <button type="button" class="admin-btn" data-page-prev ${page <= 1 ? 'disabled' : ''}>←</button>
          <span class="admin-muted">Page ${page} of ${totalPages}</span>
          <button type="button" class="admin-btn" data-page-next ${page >= totalPages ? 'disabled' : ''}>→</button>
        </div>
        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th></th>
                ${[['title', 'Title'], ['user', 'Explorer'], ['category', 'Category'],
                  ['location', 'Location'], ['created_at', 'Created']]
                  .map(([col, label]) => `<th class="admin-sortable" data-sort-yoinks="${col}">${label}${arrow(col)}</th>`).join('')}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${pageRows.length ? pageRows.map((y) => {
                const img = y.images?.length
                  ? (y.images[0].startsWith('http') ? y.images[0] : gateway.getPublicUrl('yoinks', y.images[0]))
                  : null;
                return `
                <tr>
                  <td>${img ? `<img class="admin-thumb" src="${img}" alt="" loading="lazy">`
                    : `<span class="admin-thumb admin-thumb-blank">${y.categoryEmoji}</span>`}</td>
                  <td class="admin-cell-clamp">${escapeHtml(y.title)}</td>
                  <td><button type="button" class="admin-link" data-pick-for="user" data-yoink="${y.id}">
                    ${escapeHtml(y.userName)} ▾</button></td>
                  <td><button type="button" class="admin-link" data-pick-for="category" data-yoink="${y.id}">
                    ${y.categoryEmoji} ${escapeHtml(y.categoryName)}${y.personal ? ' <span class="admin-cell-dim">(personal)</span>' : ''} ▾</button></td>
                  <td class="admin-cell-clamp admin-cell-dim">${escapeHtml(y.locationName)}</td>
                  <td class="admin-cell-dim">${new Date(y.created_at).toLocaleDateString(undefined, { timeZone: 'America/Los_Angeles' })}</td>
                  <td>
                    <div class="admin-actions">
                      <button type="button" class="admin-btn admin-btn-xs" data-view-yoink="${y.id}">View</button>
                      <button type="button" class="admin-btn admin-btn-xs admin-btn-danger"
                        data-delete-yoink="${y.id}" data-yoink-title="${escapeHtml(y.title)}">Delete</button>
                    </div>
                  </td>
                </tr>`;
              }).join('') : '<tr><td colspan="7" class="admin-empty-cell">No yoinks found</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>
      ${picker ? `
      <dialog class="v2-auth-dialog admin-transfer-dialog" data-picker-dialog>
        <h2 class="v2-dialog-title">${picker.kind === 'user' ? 'Move to another explorer' : 'Change category'}</h2>
        <input type="search" data-picker-search aria-label="Search" placeholder="Search…" autocomplete="off"
          value="${escapeHtml(picker.term || '')}">
        <div class="admin-transfer-list" data-picker-list>${pickerListHtml()}</div>
        <div class="admin-confirm-actions">
          <button type="button" class="admin-btn" data-picker-cancel>Cancel</button>
        </div>
      </dialog>` : ''}`;

    const dialog = el.querySelector('[data-picker-dialog]');
    if (dialog && typeof dialog.showModal === 'function') dialog.showModal();

    el.querySelector('[data-yoink-search]')?.addEventListener('input', debounce((e) => {
      const caret = e.target.selectionStart;
      search = e.target.value;
      page = 1;
      render();
      const again = el.querySelector('[data-yoink-search]');
      if (again) { again.focus(); again.setSelectionRange(caret, caret); }
    }, 300));
    el.querySelector('[data-picker-search]')?.addEventListener('input', debounce((e) => {
      picker.term = e.target.value;
      const listEl = el.querySelector('[data-picker-list]');
      if (listEl) listEl.innerHTML = pickerListHtml();
    }, 200));
  }

  async function transferYoink(yoinkId, newUserId, newUserName) {
    const yoink = all.find((y) => y.id === yoinkId);
    if (!yoink || yoink.user_id === newUserId) { picker = null; return render(); }
    // A personal filing can't follow the yoink — the shelf belongs to
    // the OLD owner. It unfiles on transfer (the row goes Uncategorized).
    if (!(await confirmDialog({
      title: `Transfer "${yoink.title}"?`,
      message: `It will belong to ${newUserName} from now on.${yoink.personal
        ? ' Its personal-category filing stays behind (the shelf is the old owner’s), so it arrives Uncategorized.'
        : ''}`,
      confirmLabel: 'Transfer',
    }))) return;
    const body = { user_id: newUserId };
    if (yoink.personal_category_id) body.personal_category_id = null;
    const { error } = await gateway.rawUpdate(gateway.TABLES.YOINKS, {
      filters: { id: `eq.${yoinkId}` }, body,
    });
    if (destroyed) return;
    if (error) return showToast('Couldn’t transfer the yoink.', { type: 'info' });
    yoink.user_id = newUserId;
    yoink.userName = newUserName;
    if (yoink.personal) {
      yoink.personal = false;
      yoink.personal_category_id = null;
      yoink.categoryName = 'Uncategorized';
      yoink.categoryEmoji = '📍';
    }
    picker = null;
    showToast('Yoink transferred.', { type: 'success' });
    render();
  }

  /**
   * One picker, both directions (§11): a site category clears the
   * personal filing, a personal category clears the site one — the
   * yoinks_one_category_kind rule, kept client-side too so the check
   * constraint never has to reject us.
   */
  async function recategorize(yoinkId, { catId = null, shelfId = null }) {
    const yoink = all.find((y) => y.id === yoinkId);
    const cat = catId ? catsForEdit.find((c) => c.id === catId) : null;
    const shelf = shelfId ? shelves.find((s) => s.id === shelfId) : null;
    if (!yoink || (!cat && !shelf)
      || (catId && yoink.category_id === catId)
      || (shelfId && yoink.personal_category_id === shelfId)) { picker = null; return render(); }
    const body = cat
      ? { category_id: catId, ...(yoink.personal_category_id ? { personal_category_id: null } : {}) }
      : { category_id: null, personal_category_id: shelfId };
    const { error } = await gateway.rawUpdate(gateway.TABLES.YOINKS, {
      filters: { id: `eq.${yoinkId}` }, body,
    });
    if (destroyed) return;
    if (error) return showToast('Couldn’t change the category.', { type: 'info' });
    yoink.category_id = cat ? catId : null;
    yoink.personal_category_id = shelf ? shelfId : null;
    yoink.personal = !!shelf;
    yoink.categoryName = cat ? cat.name : shelf.name;
    yoink.categoryEmoji = cat ? (cat.emoji || '📍') : (shelf.emoji || '🔒');
    picker = null;
    showToast('Category updated.', { type: 'success' });
    render();
  }

  /**
   * The picker's create row: a new shelf owned by the YOINK's owner
   * (never the admin), then the ordinary recategorize onto it. Errors
   * surface as a toast — the commonest is 20260811f not applied yet,
   * which arrives as an RLS 403 on the insert.
   */
  async function createShelfForOwner(name) {
    const yoink = all.find((y) => y.id === picker?.yoinkId);
    if (!yoink || !name) return;
    try {
      const created = await createPersonalCategoryFor(yoink.user_id, name);
      if (destroyed || !created) return;
      shelves = [...shelves, created];
      return recategorize(yoink.id, { shelfId: created.id });
    } catch (err) {
      showToast(err?.message || 'Couldn’t create that category.', { type: 'info' });
    }
  }

  async function deleteYoink(yoinkId, title) {
    if (!(await confirmDialog({
      title: `Delete "${title}"?`,
      message: 'The yoink and its photos are gone for good.',
    }))) return;
    const yoink = all.find((y) => y.id === yoinkId);
    const paths = (yoink?.images || []).filter((img) => img && !img.startsWith('http'));
    if (paths.length) {
      try { await gateway.deleteFile('yoinks', paths); } catch { /* row delete still proceeds */ }
    }
    const { error } = await gateway.rawDelete(gateway.TABLES.YOINKS, {
      filters: { id: `eq.${yoinkId}` },
    });
    if (destroyed) return;
    if (error) return showToast('Couldn’t delete the yoink.', { type: 'info' });
    all = all.filter((y) => y.id !== yoinkId);
    showToast('Yoink deleted.', { type: 'success' });
    render();
  }

  async function openPicker(kind, yoinkId) {
    picker = { kind, yoinkId, term: '' };
    render(); // dialog opens with a loading beat while the roster arrives
    if (kind === 'user') await ensureUsers();
    else await ensureCats();
    if (destroyed || !picker) return;
    const listEl = el.querySelector('[data-picker-list]');
    if (listEl) listEl.innerHTML = pickerListHtml();
    setTimeout(() => el.querySelector('[data-picker-search]')?.focus(), 50);
  }

  function onClick(event) {
    const t = event.target;
    const sortTh = t.closest('[data-sort-yoinks]');
    if (sortTh) {
      const col = sortTh.dataset.sortYoinks;
      if (sortColumn === col) sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      else { sortColumn = col; sortDirection = col === 'created_at' ? 'desc' : 'asc'; }
      page = 1;
      return render();
    }
    if (t.closest('[data-page-prev]')) { if (page > 1) { page--; render(); } return; }
    if (t.closest('[data-page-next]')) { page++; return render(); }
    const pickFor = t.closest('[data-pick-for]');
    if (pickFor) return openPicker(pickFor.dataset.pickFor, pickFor.dataset.yoink);
    if (t.closest('[data-picker-cancel]')) {
      el.querySelector('[data-picker-dialog]')?.close?.();
      picker = null;
      return render();
    }
    const pickUser = t.closest('[data-pick-user]');
    if (pickUser) return transferYoink(picker.yoinkId, pickUser.dataset.pickUser, pickUser.dataset.pickName);
    const pickCat = t.closest('[data-pick-cat]');
    if (pickCat) return recategorize(picker.yoinkId, { catId: pickCat.dataset.pickCat });
    const pickShelf = t.closest('[data-pick-shelf]');
    if (pickShelf) return recategorize(picker.yoinkId, { shelfId: pickShelf.dataset.pickShelf });
    const createShelf = t.closest('[data-picker-create-shelf]');
    if (createShelf) return createShelfForOwner(createShelf.dataset.pickerCreateShelf);
    const viewBtn = t.closest('[data-view-yoink]');
    if (viewBtn) return router.navigate('yoink', { id: viewBtn.dataset.viewYoink });
    const deleteBtn = t.closest('[data-delete-yoink]');
    if (deleteBtn) return deleteYoink(deleteBtn.dataset.deleteYoink, deleteBtn.dataset.yoinkTitle);
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch(() => { if (!destroyed) el.innerHTML = '<div class="v2-error">Couldn’t load the yoinks.</div>'; });

  el.addEventListener('click', onClick);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
  };
}
